import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api.ts'
import { useAuth } from '../context/AuthContext.tsx'
import { isSystemAdmin } from '../utils/roles.ts'
import AgencyAdminAccess from '../components/AgencyAdminAccess.tsx'
import MessageBanner from '../components/MessageBanner.tsx'
import { TableHeader, TableContainer } from '../components/Skeleton.tsx'
import { useColumnResize } from '../hooks/useColumnResize.ts'
import {
  Building2,
  Plus,
  Search,
  Target,
  Eye,
  Link,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'

interface Company {
  id: string
  name: string
  google_place_id: string
  type: string
  industries: {
    id: string
    name: string
  } | null
  address_line?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  created_at: string
}

interface Keyword {
  id: string
  keyword: string
  created_at: string
}

interface CompanyKeyword {
  id: string
  is_active: number
  companies: {
    id: string
    name: string
  }
  keywords: {
    id: string
    keyword: string
  }
  created_at: string
}

const AgencyCompanyManagement: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [companies, setCompanies] = useState<Company[]>([])
  const [keywords, setKeywords] = useState<Keyword[]>([])
  const [companyKeywords, setCompanyKeywords] = useState<CompanyKeyword[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  // Keyword form state
  const [keywordForm, setKeywordForm] = useState({
    keyword: '',
    company_id: '',
    existing_keyword_id: '',
  })

  // Industry form state
  const [industryForm, setIndustryForm] = useState({
    name: '',
  })

  // Form visibility — auto-open if ?action=add
  const [showCompanyForm, setShowCompanyForm] = useState(searchParams.get('action') === 'add')
  const [showKeywordForm, setShowKeywordForm] = useState(false)
  const [showIndustryForm, setShowIndustryForm] = useState(false)

  // Keyword form mode
  const [useExistingKeyword, setUseExistingKeyword] = useState(false)

  // Tab management
  const [activeTab, setActiveTab] = useState<'companies' | 'keywords'>('companies')

  const handleKeywordModeToggle = (useExisting: boolean) => {
    setUseExistingKeyword(useExisting)
    // Reset the form fields when switching modes
    setKeywordForm(prev => ({
      ...prev,
      keyword: '',
      existing_keyword_id: '',
    }))
  }

  const toggleKeywordActive = async (companyId: string, ckId: string, currentStatus: number) => {
    try {
      await api.patch(`/api/company/${companyId}/keywords/${ckId}`, {
        is_active: currentStatus ? 0 : 1,
      })
      // Optimistic update
      setCompanyKeywords(prev =>
        prev.map(ck => (ck.id === ckId ? { ...ck, is_active: currentStatus ? 0 : 1 } : ck)),
      )
      setMessage(`Keyword ${currentStatus ? 'deactivated' : 'activated'} successfully`)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle keyword status')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [companiesRes, keywordsRes, companyKeywordsRes] = await Promise.all([
        api.get('/api/agency/companies'),
        api.get('/api/agency/keywords'),
        api.get('/api/agency/company-keywords'),
      ])

      setCompanies(companiesRes.data.companies || [])
      setKeywords(keywordsRes.data.keywords || [])
      setCompanyKeywords(companyKeywordsRes.data.companyKeywords || [])
    } catch (error: any) {
      console.error('Error fetching data:', error)
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleIndustrySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')
    setError('')

    try {
      await api.post('/api/agency/industries', industryForm)

      setMessage('Industry added successfully!')
      setIndustryForm({ name: '' })
      setShowIndustryForm(false)
      fetchData() // Refresh industries list
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to add industry')
    } finally {
      setLoading(false)
    }
  }

  const handleKeywordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setLoading(true)
    setMessage('')
    setError('')

    try {
      if (useExistingKeyword) {
        // Link existing keyword to company
        await api.post('/api/agency/company-keywords', {
          keyword_id: keywordForm.existing_keyword_id,
          company_id: keywordForm.company_id,
        })

        setMessage('Existing keyword linked to company successfully!')
      } else {
        // Create new keyword and link to company
        await api.post('/api/agency/keywords', {
          keyword: keywordForm.keyword,
          companyIds: [keywordForm.company_id],
        })

        setMessage('New keyword created and linked to company successfully!')
      }
      setKeywordForm({
        keyword: '',
        company_id: '',
        existing_keyword_id: '',
      })
      setShowKeywordForm(false)
      setUseExistingKeyword(false)
      fetchData()
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to add keyword')
    } finally {
      setLoading(false)
    }
  }

  const handleKeywordFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    setKeywordForm(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  return (
    <AgencyAdminAccess>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Companies</h1>
            <p className="text-gray-600">Manage companies and keywords for your agency</p>
          </div>

          <MessageBanner message={message} error={error} />

          {/* Summary Cards - Moved to top */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Companies</p>
                  <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
                </div>
                <Target className="h-8 w-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Keywords</p>
                  <p className="text-2xl font-bold text-gray-900">{keywords.length}</p>
                </div>
                <Search className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Linked Keywords</p>
                  <p className="text-2xl font-bold text-gray-900">{companyKeywords.length}</p>
                </div>
                <Eye className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('companies')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'companies'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Building2 className="h-5 w-5" />
                    <span>Companies</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('keywords')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'keywords'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Search className="h-5 w-5" />
                    <span>Keyword Management</span>
                  </div>
                </button>
              </nav>
            </div>
          </div>

          <div className="space-y-8">
            {/* Companies Tab Content */}
            {activeTab === 'companies' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <h2 className="text-xl font-semibold text-gray-900">Companies</h2>
                    </div>
                    <button
                      onClick={() => setShowCompanyForm(!showCompanyForm)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={16} />
                      <span>{showCompanyForm ? 'Close' : 'Add Company'}</span>
                    </button>
                  </div>
                </div>

                {showCompanyForm && (
                  <CompanyImportForm
                    onComplete={() => {
                      setShowCompanyForm(false)
                      fetchData()
                    }}
                    onCancel={() => setShowCompanyForm(false)}
                  />
                )}

                {/* Industry Form */}
                {showIndustryForm && (
                  <div className="p-6 border-b border-gray-200">
                    <form onSubmit={handleIndustrySubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Industry Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={industryForm.name}
                          onChange={e => setIndustryForm({ name: e.target.value })}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter industry name"
                        />
                      </div>
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => setShowIndustryForm(false)}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          {loading ? 'Adding...' : 'Add Industry'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="p-6">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="text-gray-500">Loading companies...</div>
                    </div>
                  ) : (
                    <CompaniesTable companies={companies} navigate={navigate} />
                  )}
                </div>
              </div>
            )}

            {/* Keywords Tab Content */}
            {activeTab === 'keywords' && (
              <div className="space-y-6">
                {/* Companies Overview Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <h2 className="text-xl font-semibold text-gray-900">Companies Overview</h2>
                    </div>
                    <p className="text-gray-600 mb-4">
                      View all companies and their associated keywords in one place.
                    </p>

                    {loading ? (
                      <div className="text-center py-8">
                        <div className="text-gray-500">Loading companies...</div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {companies.map(company => {
                          // Get keywords for this company
                          const companyKeywordsForCompany = companyKeywords.filter(
                            ck => ck.companies.id === company.id,
                          )

                          return (
                            <div
                              key={company.id}
                              className="bg-white border border-gray-200 rounded-lg p-4"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="p-2 bg-blue-100 rounded-full">
                                    <Building2 className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-gray-900">{company.name}</h3>
                                    <p className="text-sm text-gray-600">
                                      <span className="font-medium">Industry:</span>{' '}
                                      {company.industries?.name || 'No Industry'} •
                                      <span className="font-medium ml-1">Type:</span> {company.type}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs text-gray-500 mb-1">Created</div>
                                  <div className="text-sm font-medium text-gray-700">
                                    {company.created_at
                                      ? new Date(company.created_at).toLocaleDateString()
                                      : 'No Date'}
                                  </div>
                                </div>
                              </div>

                              {/* Keywords for this company */}
                              <div className="border-t border-gray-200 pt-3">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium text-gray-700">
                                    Keywords ({companyKeywordsForCompany.length})
                                  </h4>
                                </div>
                                {companyKeywordsForCompany.length > 0 ? (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {companyKeywordsForCompany.map(ck => (
                                      <div
                                        key={ck.id}
                                        className={`flex items-center justify-between p-2 rounded border ${
                                          ck.is_active
                                            ? 'bg-green-50 border-green-200'
                                            : 'bg-gray-50 border-gray-200'
                                        }`}
                                      >
                                        <div className="flex items-center space-x-2">
                                          <Search
                                            className={`h-4 w-4 ${ck.is_active ? 'text-green-600' : 'text-gray-400'}`}
                                          />
                                          <span
                                            className={`text-sm ${ck.is_active ? 'text-gray-700' : 'text-gray-400 line-through'}`}
                                          >
                                            {ck.keywords?.keyword || 'Unknown'}
                                          </span>
                                        </div>
                                        <button
                                          onClick={() =>
                                            toggleKeywordActive(company.id, ck.id, ck.is_active)
                                          }
                                          className={`p-1 rounded transition-colors ${
                                            ck.is_active
                                              ? 'text-green-600 hover:text-green-800 hover:bg-green-100'
                                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                          }`}
                                          title={
                                            ck.is_active ? 'Deactivate keyword' : 'Activate keyword'
                                          }
                                        >
                                          {ck.is_active ? (
                                            <ToggleRight size={18} />
                                          ) : (
                                            <ToggleLeft size={18} />
                                          )}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-3 text-gray-500 text-sm">
                                    No keywords linked yet. Add keywords below to track performance.
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                        {companies.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <Building2 className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                              No Companies Yet
                            </h3>
                            <p className="text-gray-500">
                              Add companies first to begin tracking keywords.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Keyword Management Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <h2 className="text-xl font-semibold text-gray-900">Keyword Management</h2>
                      </div>
                      <button
                        onClick={() => setShowKeywordForm(!showKeywordForm)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Plus size={16} />
                        <span>Add Keyword</span>
                      </button>
                    </div>
                  </div>

                  {showKeywordForm && (
                    <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                      <form onSubmit={handleKeywordSubmit} className="space-y-6">
                        {/* Keyword Mode Toggle */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            Keyword Management Mode
                          </h4>
                          <div className="flex items-center space-x-6">
                            <label className="flex items-center cursor-pointer group">
                              <input
                                type="radio"
                                name="keywordMode"
                                checked={!useExistingKeyword}
                                onChange={() => handleKeywordModeToggle(false)}
                                className="mr-3 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                  <Plus className="h-4 w-4 text-blue-600" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  Create New Keyword
                                </span>
                              </div>
                            </label>
                            <label className="flex items-center cursor-pointer group">
                              <input
                                type="radio"
                                name="keywordMode"
                                checked={useExistingKeyword}
                                onChange={() => handleKeywordModeToggle(true)}
                                className="mr-3 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                  <Link className="h-4 w-4 text-green-600" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  Link Existing Keyword
                                </span>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Mode Indicator */}
                        <div
                          className={`p-4 rounded-lg border-l-4 ${
                            useExistingKeyword
                              ? 'bg-green-50 border-green-400'
                              : 'bg-blue-50 border-blue-400'
                          }`}
                        >
                          <div className="flex items-start">
                            <div
                              className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-0.5 ${
                                useExistingKeyword ? 'bg-green-100' : 'bg-blue-100'
                              }`}
                            >
                              {useExistingKeyword ? (
                                <Link className="h-3 w-3 text-green-600" />
                              ) : (
                                <Plus className="h-3 w-3 text-blue-600" />
                              )}
                            </div>
                            <div>
                              <h5
                                className={`font-medium ${
                                  useExistingKeyword ? 'text-green-800' : 'text-blue-800'
                                }`}
                              >
                                {useExistingKeyword
                                  ? 'Link Existing Keyword'
                                  : 'Create New Keyword'}
                              </h5>
                              <p
                                className={`text-sm ${
                                  useExistingKeyword ? 'text-green-700' : 'text-blue-700'
                                }`}
                              >
                                {useExistingKeyword
                                  ? 'Select an existing keyword and company to link them together.'
                                  : 'Enter a new keyword and select a company to create and link them.'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* New Keyword Input */}
                        {!useExistingKeyword && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              <div className="flex items-center space-x-2">
                                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Plus className="h-3 w-3 text-blue-600" />
                                </div>
                                <span>New Keyword *</span>
                              </div>
                            </label>
                            <input
                              type="text"
                              name="keyword"
                              value={keywordForm.keyword}
                              onChange={handleKeywordFormChange}
                              required={!useExistingKeyword}
                              placeholder="Enter keyword to track (e.g., 'local fitness training')"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              This keyword will be created and automatically linked to the selected
                              company.
                            </p>
                          </div>
                        )}

                        {/* Existing Keyword Selection */}
                        {useExistingKeyword && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              <div className="flex items-center space-x-2">
                                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                                  <Link className="h-3 w-3 text-green-600" />
                                </div>
                                <span>Select Existing Keyword *</span>
                              </div>
                            </label>
                            <select
                              name="existing_keyword_id"
                              value={keywordForm.existing_keyword_id}
                              onChange={handleKeywordFormChange}
                              required={useExistingKeyword}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                            >
                              <option value="">Choose from existing keywords...</option>
                              {keywords.map(keyword => (
                                <option key={keyword.id} value={keyword.id}>
                                  {keyword.keyword}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-2">
                              Select a keyword that's already in the system to link to a company.
                            </p>
                          </div>
                        )}

                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                                <Building2 className="h-3 w-3 text-purple-600" />
                              </div>
                              <span>Link to Company *</span>
                            </div>
                          </label>
                          <select
                            name="company_id"
                            value={keywordForm.company_id}
                            onChange={handleKeywordFormChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                          >
                            <option value="">Select a company to link with...</option>
                            {companies.map(company => (
                              <option key={company.id} value={company.id}>
                                {company.name} ({company.industries?.name || 'No Industry'})
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-2">
                            Choose which company this keyword will be associated with.
                          </p>
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowKeywordForm(false)}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={
                              loading ||
                              !keywordForm.company_id ||
                              (useExistingKeyword
                                ? !keywordForm.existing_keyword_id
                                : !keywordForm.keyword)
                            }
                            className={`px-6 py-3 rounded-lg font-medium transition-all ${
                              loading ||
                              !keywordForm.company_id ||
                              (useExistingKeyword
                                ? !keywordForm.existing_keyword_id
                                : !keywordForm.keyword)
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : useExistingKeyword
                                  ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-md'
                                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
                            }`}
                          >
                            {loading ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Processing...</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                {useExistingKeyword ? (
                                  <>
                                    <Link className="h-4 w-4" />
                                    <span>Link Keyword</span>
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-4 w-4" />
                                    <span>Add Keyword</span>
                                  </>
                                )}
                              </div>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AgencyAdminAccess>
  )
}

// ===================== Company Import Form (Spreadsheet) =====================

interface ImportRow {
  id: string
  industry: string
  companyName: string
  address: string
  keywords: string
  agency_id: string
}

interface ImportRowError {
  [field: string]: string
}

const EMPTY_ROW = (): ImportRow => ({
  id: crypto.randomUUID(),
  industry: '',
  companyName: '',
  address: '',
  keywords: '',
  agency_id: '',
})

function CompanyImportForm({
  onComplete,
  onCancel,
}: {
  onComplete: () => void
  onCancel: () => void
}) {
  const { user } = useAuth()
  const isSysAdmin = isSystemAdmin(user)
  const [rows, setRows] = useState<ImportRow[]>([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()])
  const [rowErrors, setRowErrors] = useState<Record<string, ImportRowError>>({})
  const [isValidated, setIsValidated] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [industries, setIndustries] = useState<string[]>([])
  const [agencies, setAgencies] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    api
      .get('/api/agency/industries')
      .then(res => {
        const names = (res.data.industries || []).map((i: any) => i.name)
        setIndustries(names)
      })
      .catch(() => {})
  }, [])

  // Fetch agencies when user auth resolves
  useEffect(() => {
    if (isSysAdmin) {
      api
        .get('/api/admin/agencies')
        .then(res => {
          setAgencies((res.data.agencies || []).map((a: any) => ({ id: a.id, name: a.name })))
        })
        .catch(error => {
          console.error('Error fetching agencies:', error)
          setResult({
            success: false,
            message: 'Failed to load agencies list. Please refresh and try again.',
          })
        })
    }
  }, [isSysAdmin])

  const updateRow = (id: string, field: keyof ImportRow, value: string) => {
    setRows(prev => prev.map(row => (row.id === id ? { ...row, [field]: value } : row)))
    if (isValidated) {
      setRowErrors(prev => {
        const updated = { ...prev }
        if (updated[id]) {
          const { [field]: _, ...rest } = updated[id]
          if (Object.keys(rest).length === 0) delete updated[id]
          else updated[id] = rest
        }
        return updated
      })
    }
  }

  const addRow = () => {
    setRows(prev => [...prev, EMPTY_ROW()])
    setIsValidated(false)
  }
  const removeRow = (id: string) => {
    if (rows.length <= 1) return
    setRows(prev => prev.filter(row => row.id !== id))
    setRowErrors(prev => {
      const u = { ...prev }
      delete u[id]
      return u
    })
  }

  const validateRows = (): boolean => {
    const errors: Record<string, ImportRowError> = {}
    let hasErrors = false
    const nonEmpty = rows.filter(r => r.industry || r.companyName || r.address || r.keywords)
    if (nonEmpty.length === 0) {
      setRowErrors({})
      setIsValidated(true)
      return false
    }

    for (const row of nonEmpty) {
      const fe: ImportRowError = {}
      if (!row.companyName.trim()) fe.companyName = 'Required'
      if (!row.address.trim()) fe.address = 'Required'
      if (!row.keywords.trim()) fe.keywords = 'Required'
      else if (
        row.keywords
          .split(',')
          .map(k => k.trim())
          .filter(Boolean).length === 0
      )
        fe.keywords = 'At least one keyword'
      if (Object.keys(fe).length > 0) {
        errors[row.id] = fe
        hasErrors = true
      }
    }

    const seen = new Set<string>()
    for (const row of nonEmpty) {
      const lower = row.companyName.trim().toLowerCase()
      if (lower && seen.has(lower)) {
        if (!errors[row.id]) errors[row.id] = {}
        errors[row.id].companyName = 'Duplicate name'
        hasErrors = true
      }
      seen.add(lower)
    }
    setRowErrors(errors)
    setIsValidated(true)
    return !hasErrors
  }

  const handleSubmit = async () => {
    if (!validateRows()) return
    const nonEmpty = rows.filter(r => r.companyName || r.address || r.keywords)
    setUploading(true)
    setResult(null)
    try {
      const response = await api.post('/api/admin/import-companies-manual', {
        rows: nonEmpty.map(r => ({
          industry: r.industry.trim(),
          companyName: r.companyName.trim(),
          address: r.address.trim(),
          keywords: r.keywords.trim(),
          ...(isSysAdmin && { agency_id: r.agency_id }),
        })),
      })
      setResult(response.data)
      if (response.data.success) {
        setTimeout(() => onComplete(), 1500)
      }
    } catch (error: any) {
      setResult({ success: false, message: error.response?.data?.message || 'Import failed.' })
    } finally {
      setUploading(false)
    }
  }

  const nonEmptyCount = rows.filter(
    r => r.industry || r.companyName || r.address || r.keywords,
  ).length
  const errorCount = Object.keys(rowErrors).length

  return (
    <div className="p-6 border-b border-gray-200 bg-gray-50">
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-sm">
          Enter company details below. Separate multiple keywords with commas.
        </p>
      </div>

      <div className="overflow-x-auto mb-4">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 w-8">
                #
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 min-w-[140px]">
                Industry
              </th>
              {isSysAdmin && (
                <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 min-w-[140px]">
                  Agency
                </th>
              )}
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 min-w-[170px]">
                Company Name <span className="text-red-500">*</span>
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 min-w-[240px]">
                Address <span className="text-red-500">*</span>
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 min-w-[260px]">
                Keywords (comma-separated) <span className="text-red-500">*</span>
              </th>
              <th className="border border-gray-300 px-3 py-2 text-center text-xs font-semibold text-gray-700 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const errors = rowErrors[row.id] || {}
              return (
                <tr
                  key={row.id}
                  className={
                    Object.keys(errors).length > 0
                      ? 'bg-red-50'
                      : index % 2 === 0
                        ? 'bg-white'
                        : 'bg-gray-50'
                  }
                >
                  <td className="border border-gray-300 px-3 py-1 text-xs text-gray-500 text-center font-mono">
                    {index + 1}
                  </td>
                  {/* Industry — combo: dropdown of existing + type new */}
                  <td className="border border-gray-300 p-0">
                    <input
                      type="text"
                      list={`industry-list-${row.id}`}
                      value={row.industry}
                      onChange={e => updateRow(row.id, 'industry', e.target.value)}
                      placeholder="Select or type new..."
                      className={`w-full px-3 py-2 text-sm border-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${errors.industry ? 'bg-red-50 text-red-800' : ''}`}
                    />
                    <datalist id={`industry-list-${row.id}`}>
                      {industries.map(name => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                    {errors.industry && (
                      <p className="px-3 pb-1 text-xs text-red-600">{errors.industry}</p>
                    )}
                  </td>
                  {/* Agency — only for system admins */}
                  {isSysAdmin && (
                    <td className="border border-gray-300 p-0">
                      <select
                        value={row.agency_id}
                        onChange={e => updateRow(row.id, 'agency_id', e.target.value)}
                        className="w-full px-3 py-2 text-sm border-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                      >
                        <option value="">None</option>
                        {agencies.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  {/* Company Name, Address, Keywords */}
                  {(['companyName', 'address', 'keywords'] as const).map(field => (
                    <td key={field} className="border border-gray-300 p-0">
                      <input
                        type="text"
                        value={row[field]}
                        onChange={e => updateRow(row.id, field, e.target.value)}
                        placeholder={
                          field === 'companyName'
                            ? 'e.g. Acme Inc'
                            : field === 'address'
                              ? '123 Main St, City, ST 12345'
                              : 'keyword1, keyword2, ...'
                        }
                        className={`w-full px-3 py-2 text-sm border-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${errors[field] ? 'bg-red-50 text-red-800' : ''}`}
                      />
                      {errors[field] && (
                        <p className="px-3 pb-1 text-xs text-red-600">{errors[field]}</p>
                      )}
                    </td>
                  ))}
                  <td className="border border-gray-300 px-2 py-1 text-center">
                    {rows.length > 1 && (
                      <button
                        onClick={() => removeRow(row.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <span className="text-lg">×</span>
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={addRow}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          <Plus size={14} /> Add Row
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {nonEmptyCount} {nonEmptyCount === 1 ? 'company' : 'companies'}
            {errorCount > 0 && (
              <span className="text-red-500 ml-1">
                · {errorCount} {errorCount === 1 ? 'error' : 'errors'}
              </span>
            )}
          </span>
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading || nonEmptyCount === 0}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${uploading || nonEmptyCount === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {uploading ? 'Importing...' : 'Import Companies'}
          </button>
        </div>
      </div>

      {result && (
        <div
          className={`mt-4 p-4 rounded-lg border ${result.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
        >
          <p className="font-medium">{result.message}</p>
          {result.summary && (
            <p className="text-sm mt-1">
              {result.summary.successfulRows} imported, {result.summary.failedRows} failed
            </p>
          )}
          {result.errors?.length > 0 && (
            <ul className="text-sm mt-2">
              {result.errors.map((e: string, i: number) => (
                <li key={i}>• {e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default AgencyCompanyManagement

// ===================== Companies Table =====================

function CompaniesTable({
  companies,
  navigate,
}: {
  companies: Company[]
  navigate: (path: string) => void
}) {
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [search, setSearch] = useState('')
  const { widths, startResize } = useColumnResize('agency-companies-col-widths')

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  const filtered = companies.filter(c => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      c.name.toLowerCase().includes(q) ||
      (c.industries?.name || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q) ||
      (c.state || '').toLowerCase().includes(q)
    )
  })

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    switch (sortBy) {
      case 'industry':
        return (a.industries?.name || '').localeCompare(b.industries?.name || '') * dir
      case 'location':
        return (a.state || '').localeCompare(b.state || '') * dir
      case 'created_at': {
        const at = a.created_at ? new Date(a.created_at).getTime() : 0
        const bt = b.created_at ? new Date(b.created_at).getTime() : 0
        return (at - bt) * dir
      }
      default:
        return a.name.localeCompare(b.name) * dir
    }
  })

  return (
    <div>
      <div className="mb-4 max-w-sm relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search companies..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <TableContainer
        loading={false}
        hasData={sorted.length > 0}
        skeletonRows={5}
        skeletonCols={5}
        emptyMessage={search ? 'No companies match your search' : 'No companies yet'}
      >
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table
            className={`min-w-full divide-y divide-gray-200 ${Object.keys(widths).length > 0 ? 'table-fixed' : ''}`}
          >
            <TableHeader
              columns={[
                { label: 'Company', key: 'name' },
                { label: 'Industry', key: 'industry' },
                { label: 'Location', key: 'location' },
                { label: 'Created', key: 'created_at' },
                { label: '', key: 'actions', sortable: false, resizable: false },
              ]}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={toggleSort}
              widths={widths}
              onResize={startResize}
            />
            <tbody className="bg-white divide-y divide-gray-200">
              {sorted.map(company => (
                <tr
                  key={company.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/company/${company.id}`)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{company.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {company.industries?.name || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {[company.city, company.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {company.created_at
                      ? new Date(company.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        navigate(`/company/${company.id}`)
                      }}
                      className="text-blue-600 hover:text-blue-800"
                      title="View Dashboard"
                    >
                      <ExternalLink size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableContainer>
    </div>
  )
}
